/*
 * Which way the back camera faces, as a compass bearing (degrees clockwise
 * from north): Field Mode's capture telemetry (migration 0090,
 * docs/FIELD_AI_VISION_SPEC.md Part I section 4). Pure, so Vitest checks it
 * on known orientations (heading.test.js).
 *
 * utils/events/scan.js::headingOf gives the bearing of the phone's TOP EDGE,
 * which is right for a phone held flat like a compass. A camera is held
 * upright or tilted down at the ground, and then the top edge points at the
 * sky: the bearing that matters is the back camera's, the device's -z axis.
 *
 * DeviceOrientation's alpha, beta, gamma are intrinsic Z-X'-Y'' rotations of
 * the device frame from the Earth frame (x east, y north, z up), alpha
 * counted anticlockwise (W3C DeviceOrientation Event). Rotating -z by
 * R = Rz(alpha) Rx(beta) Ry(gamma) gives the camera's direction in the Earth
 * frame; its horizontal part's bearing is the answer.
 *
 * Only an ABSOLUTE alpha means anything (Chrome's deviceorientationabsolute,
 * or `absolute: true`); a relative one counts from wherever the page started.
 * iOS gives webkitCompassHeading instead, as scan.js already reads it.
 *
 * Pointed almost straight down (or up) the camera has no bearing worth
 * keeping: below MIN_HORIZONTAL of its direction lying flat, null.
 */
import { normalise } from '../events/scan';

export const MIN_HORIZONTAL = 0.15;

const RAD = Math.PI / 180;

/** The back camera's bearing from absolute alpha, beta, gamma (degrees), or null. */
export function bearingFromAngles(alpha, beta, gamma) {
  if (![alpha, beta, gamma].every(Number.isFinite)) return null;
  const [cA, sA] = [Math.cos(alpha * RAD), Math.sin(alpha * RAD)];
  const sB = Math.sin(beta * RAD);
  const [cG, sG] = [Math.cos(gamma * RAD), Math.sin(gamma * RAD)];
  // -(R * [0, 0, 1]): the back camera's direction, east and north parts
  // (its up part, -cos(beta) cos(gamma), is not needed for a bearing).
  const east = -(cA * sG + sA * sB * cG);
  const north = -(sA * sG - cA * sB * cG);
  if (Math.hypot(east, north) < MIN_HORIZONTAL) return null;
  return normalise(Math.atan2(east, north) / RAD);
}

/** A DeviceOrientation event's camera bearing, or null when it has none. */
export function cameraBearing(event) {
  if (!event) return null;
  if (Number.isFinite(event.webkitCompassHeading)) return normalise(event.webkitCompassHeading);
  if (!event.absolute) return null;
  return bearingFromAngles(event.alpha, event.beta, event.gamma);
}

/** A bearing as photos.capture_heading_deg takes it: one decimal, under 360. */
export function headingForPhoto(bearing) {
  if (!Number.isFinite(bearing)) return null;
  return Math.min(Math.round(normalise(bearing) * 10) / 10, 359.9);
}
